/**
 * Ryan O'Dowd
 * 2023-05-02
 * © Copyright 2023 Oakwood Software Consulting, Inc. All Rights Reserved.
 */
import React, {
  useCallback,
  // @TODO: useEffect,
  useMemo,
  useState,
} from 'react';
import {
  TouchableOpacity,
  TouchableWithoutFeedback,
  View,
} from 'react-native';
import Animated, {
  // @TODO: Easing,
  // @TODO: useSharedValue,
  // @TODO: useAnimatedStyle,
  // @TODO: useAnimatedProps,
  // @TODO: withRepeat,
  // @TODO: withTiming,
} from 'react-native-reanimated';
import CPassage from '@classes/Passage';
import Popover from '@components/common/Popover';
import Skeleton from '@components/common/Skeleton';
import Text from '@components/common/Text';
import WrinkleBadge from '@components/WrinkleBadge';
import getStyles from './styles';
import globals from '@utils/globals';
import hooks from '@hooks';
import utilities from '@utils/utilities';

const Verse = (_p: VerseProps) => {
  const props = {
    ...defaultProps,
    ..._p,
  };

  const styles = hooks.useStyles(getStyles);

  // @TODO: const animatedBorderRightWidth = useSharedValue(4);
  // @TODO: const animatedBorderRightHeight = useSharedValue(10);

  // @TODO: const animatedStyles = useAnimatedStyle(() => ({
    // @TODO: borderRightWidth: animatedBorderRightWidth.value,
    // @TODO: lineHeight: animatedBorderRightHeight.value,
    // @TODO: borderTopRightRadius: animatedBorderRightHeight.value,
    // @TODO: borderBottomRightRadius: animatedBorderRightHeight.value,
  // @TODO: }), []);

  // @TODO: const handlePress = () => {};

  const [_isWrinkleBadgeOpen, setIsWrinkleBadgeOpen] = useState(false);

  const [bookApiId, chapterNumber, verseNumber] = props.verseApiId.split('.');

  const wrinkles = hooks.useGetWrinklesForVerseApiId(props.verseApiId, props.translationId);
  const thisTranslationWrinkle = wrinkles.find((w) => w.translation_id === props.translationId);

  const _isWrinkleOpen = (wrinkle) => {
    return wrinkle?.level_number && Date.now() >= new Date(wrinkle.date_next_srs_level).getTime();
  };

  /* @TODO:
  useEffect(() => {
    if (!thisTranslationWrinkle) {
      return;
    }

    const wrinkleOpen = Date.now() >= new Date(thisTranslationWrinkle.date_next_srs_level).getTime();

    if (!wrinkleOpen) {
      return;
    }

    /* @TODO:
    animatedBorderRightHeight.value = withRepeat(
      withTiming(animatedBorderRightHeight.value === 40 ? 0 : 40, {
        duration: 1100,
        easing: Easing.inOut(Easing.quad),
        // @TODO: reduceMotion: ReduceMotion.System, // @TODO: why isn't eslint complaining about ReduceMotion not defined?
      }),
      -1,
      true, // @TODO: should this be false? https://docs.swmansion.com/react-native-reanimated/docs/animations/withRepeat
    );
    animatedBorderRightHeight.value = withRepeat(
      withTiming(animatedBorderRightHeight.value === 1 ? 100 : 1, {
        duration: 1100,
        easing: Easing.inOut(Easing.quad),
        // @TODO: reduceMotion: ReduceMotion.System, // @TODO: why isn't eslint complaining about ReduceMotion not defined?
      }),
      -1,
      true, // @TODO: should this be false? https://docs.swmansion.com/react-native-reanimated/docs/animations/withRepeat
    );
    animatedBorderColor.value = withRepeat(
      withTiming(animatedBorderColor.value === 1 ? 100 : 1, {
        duration: 1100,
        easing: Easing.inOut(Easing.quad),
        // @TODO: reduceMotion: ReduceMotion.System, // @TODO: why isn't eslint complaining about ReduceMotion not defined?
      }),
      -1,
      true, // @TODO: should this be false? https://docs.swmansion.com/react-native-reanimated/docs/animations/withRepeat
    );
    /
    /* @TODO:
    animatedBorderRightWidth.value = withRepeat(
      withTiming(animatedBorderRightWidth.value === 2 ? 10 : 2, {
        duration: 1100,
        easing: Easing.inOut(Easing.quad),
        // @TODO: reduceMotion: ReduceMotion.System, // @TODO: why isn't eslint complaining about ReduceMotion not defined?
      }),
      -1,
      true, // @TODO: should this be false? https://docs.swmansion.com/react-native-reanimated/docs/animations/withRepeat
    );
    /
  }, []);
  */

  const book = CPassage.getBookFromApiId(bookApiId);

  const passageId = -1;
  const verses = hooks.useGetVersesForPassages([new CPassage(
    passageId,
    +props.translationId,
    +book?.id,
    +chapterNumber,
    +verseNumber,
    +verseNumber, // doesn't need to be props.rangeEndVerseNumber; this is handled by useGetVersesForPassages
  )], props.skipFetch)[passageId];
  const verse = verses[0];
  const footnotes = useMemo(() => {
    return verses.flatMap((v, i) => {
      return (
        v.footnotes?.map((f) => {
          return {
            ...f,
            position_in_verse_text: i === 0 ? f.position_in_verse_text : verse.verseText.length, // this ensures that footnotes in range verses (after the first verse) are placed at the end of the verse text
          };
        }) || []
      ).sort((f1, f2) => { // 1 Chronicles 11:35 has z, aa, and ab footnotes...without sorting, it's displayed as ab, aa, z
        if (f1.indicator.length !== f2.indicator.length) {
          return f1.indicator.length - f2.indicator.length;
        }

        return f1.indicator.localeCompare(f2.indicator);
      });
    });
  }, [verses, verse.verseText]);

  const _getExtraVerseTextStyles = useCallback((wrinkle) => {
    if (wrinkle?.level_number >= globals.srsLevelWrinkleIsEarnedAt) {
      return styles.completedVerseText;
    } else if (wrinkle?.level_number >= 1) {
      return styles.inProgressVerseText;
    } else if (wrinkle?.level_number === 0) {
      return styles.unstartedVerseText;
    }

    return {};
  }, [styles]);

  const contentWithFootnotes = useCallback((contentText, isHeading = false) => {
    const numLeadingSpaces = contentText?.search(/\S/) || 0;
    let content = contentText?.trimStart() || '';
    if (!props.showFormatting) {
      content = utilities.removeVerseFormatting(content);
    }
    const contentToRender = [];
    let prevPosition = 0;
    if (props.showFootnotes) {
      for (const footnote of footnotes) {
        if ((!isHeading && footnote.is_in_heading) || (isHeading && !footnote.is_in_heading)) {
          continue;
        }

        contentToRender.push(content.substring(prevPosition, footnote.position_in_verse_text - numLeadingSpaces));
        prevPosition = footnote.position_in_verse_text - numLeadingSpaces;

        contentToRender.push(
          <TouchableOpacity
            key={footnote.id}
            style={styles.footnoteIndicatorWrapper}
          >
            <Popover.Wrapper
              popover={
                <Popover.Inner>
                  <Popover.Text title={true}>Footnote</Popover.Text>
                  <Popover.Text>{footnote.body}</Popover.Text>
                  {/* @TODO:
                  // @TODO: would also be nice to navigate to any references inside of footnote (if any)
                  */}
                </Popover.Inner>
              }
            >
              <Text style={styles.footnoteIndicator}>{`[${footnote.indicator}]`}</Text>
            </Popover.Wrapper>
          </TouchableOpacity>,
        );
      }
    }

    if (props.showFootnotes && footnotes.length) {
      contentToRender.push(content.substring(prevPosition));
    } else {
      contentToRender.push(content);
    }

    return contentToRender.filter(Boolean);
  }, [props.showFootnotes, props.showFormatting, styles, footnotes]);

  const _getExtraVerseWrapperStyles = (wrinkle) => {
    let extraStyles = {};
    if (wrinkle?.level_number >= globals.srsLevelWrinkleIsEarnedAt) {
      extraStyles = styles.completedVerseWrapper;
    } else if (wrinkle?.level_number >= 1) {
      extraStyles = styles.inProgressVerseWrapper;
    } else if (wrinkle?.level_number === 0) {
      extraStyles = styles.unstartedVerseWrapper;
    }

    if (_isWrinkleOpen(wrinkle)) {
      // @TODO: extraStyles = {...extraStyles, ...styles.openVerseWrapper};
    }

    return extraStyles;
  };

  const _getExtraVerseNumsOnlyWrapperStyles = (wrinkle) => {
    let extraStyles = {};
    if (props.isSelectionModeOn) {
      return extraStyles;
    }

    if (wrinkle?.level_number >= globals.srsLevelWrinkleIsEarnedAt) {
      extraStyles = styles.numsOnlyCompletedVerse;
    } else if (wrinkle?.level_number >= 1) {
      extraStyles = styles.numsOnlyInProgressVerse;
    } else if (wrinkle?.level_number === 0) {
      extraStyles = styles.numsOnlyUnstartedVerse;
    }

    return extraStyles;
  };

  const _getExtraVerseNumsOnlyTextStyles = (wrinkle) => {
    let extraStyles = {};
    if (wrinkle?.level_number >= globals.srsLevelWrinkleIsEarnedAt) {
      extraStyles = styles.numsOnlyCompletedVerseText;
    } else if (wrinkle?.level_number >= 1) {
      extraStyles = styles.numsOnlyInProgressVerseText;
    } else if (wrinkle?.level_number === 0) {
      extraStyles = styles.numsOnlyUnstartedVerseText;
    }

    return extraStyles;
  };

  if (props.isLoading) {
    return (
      <View style={styles.skeletonWrapper}>
        {(props.showHeadings && +verseNumber === 1) && (
          <Skeleton style={styles.skeletonHeading} />
        )}
        <Skeleton style={styles.skeletonFirstLine} />
        <Skeleton />
        <Skeleton style={styles.skeletonLastLine} />
      </View>
    );
  }

  if (props.verseNumbersOnly) {
    return (
      <React.Fragment>
        <TouchableOpacity
          style={[
            styles.numsOnlyVerseNumber,
            !props.hideProgressStyling && _getExtraVerseNumsOnlyWrapperStyles(thisTranslationWrinkle),
            props.showContextHighlight && props.isForContext && styles.highlightedVerse,
            props.isSelected && styles.numsOnlySelectedVerse,
            props.numsOnlyStyle,
          ]}
          disabled={props.isPressDisabled}
          onPress={props.isPressDisabled ? null : (props.onPressVerse || (() => setIsWrinkleBadgeOpen(true)))}
        >
          <Text
            style={[
              styles.verseNumberText,
              !props.hideProgressStyling && _getExtraVerseNumsOnlyTextStyles(thisTranslationWrinkle),
            ]}
          >
            {verseNumber}{!!props.rangeEndVerseNumber && `-${props.rangeEndVerseNumber}`}
          </Text>
        </TouchableOpacity>
        {(props.showWrinkleBadge && thisTranslationWrinkle && !props.isSelectionModeOn) &&
          <WrinkleBadge
            isOpen={_isWrinkleBadgeOpen}
            wrinkles={[thisTranslationWrinkle]}// @TODO: more should be passed in because of the dialog?
            translationIdForThisVerse={props.translationId}
            isBadgePressDisabled={props.isBadgePressDisabled}
            absoluteTop={-4}
            absoluteRight={-4}
          />
        }
      </React.Fragment>
    );
  }

  return (
    <View style={styles.container}>
      {(props.showHeadings && verse.heading) &&
        <Text style={[styles.verseWrapper, styles.verseHeading]}>{contentWithFootnotes(verse.heading, true)}</Text>
      }{/* @TODO: heading can have footnote in it */}
      <TouchableWithoutFeedback
        disabled={props.isPressDisabled}
        onPress={props.isPressDisabled ? null : props.onPressVerse}
      >
        <Animated.View
          style={[
            styles.verseWrapper,
            // @TODO: animatedStyles,
            !props.hideProgressStyling && _getExtraVerseWrapperStyles(thisTranslationWrinkle),
            props.showContextHighlight && props.isForContext && styles.highlightedVerse,
            props.isSelected && styles.selectedVerseWrapper,
            !props.showFormatting && styles.verseWrapperNoFormatting, // if there's no formatting, provide a little separation at least
          ]}
        >
          <Text
            style={[
              styles.verseNumber,
              !props.hideProgressStyling && _getExtraVerseTextStyles(thisTranslationWrinkle),
              props.isSelected && styles.selectedVerseNumber,
            ]}
          >
            {verseNumber}{!!props.rangeEndVerseNumber && `-${props.rangeEndVerseNumber}`}
          </Text>
          <Text style={[styles.verseText, props.verseTextStyles]}>
            <Text
              style={[
                styles.typedVerse,
                {color: styles.colors[props.defaultTextColor]},
                !props.hideProgressStyling && _getExtraVerseTextStyles(thisTranslationWrinkle),
                props.showContextHighlight && props.isForContext && styles.highlightedText,
                props.isSelected && styles.selectedVerseText,
                props.textColorOverride && {color: props.textColorOverride},
              ]}
            >
              {/* @TODO: this should be done in the script to populate db, not on front end
              {verse.verseText.replaceAll(/\n {2}$/g, '\n\n  ') || ''}
              */}
              {/* @TODO: if verse doesn't exist in this translation, Bible Gateway surrounds the verse number in []...not sure if i need to do that if the footnote is visible, but it's an idea */}
              {` ${+verseNumber >= 10 ? '  ' : ''}${+verseNumber >= 100 ? '  ' : ''}`}{!!props.rangeEndVerseNumber && '  '}{!!props.rangeEndVerseNumber && ` ${props.rangeEndVerseNumber >= 10 ? '  ' : ''}${(props.rangeEndVerseNumber ?? 0) >= 100 ? '  ' : ''}`}{contentWithFootnotes(verse.verseText)}
            </Text>
          </Text>
          {props.showWrinkleBadge && (
            <View style={styles.allBadgesWrapper}>
              <WrinkleBadge
                wrinkles={props.isBadgePressDisabled ? [thisTranslationWrinkle] : wrinkles}// @TODO: don't hijack isBadgePressDisabled prop to see if this is in the wrinkle badge dialog or not
                translationIdForThisVerse={props.translationId}
                positionRelative={true}
                isBadgePressDisabled={props.isBadgePressDisabled}
                badgeSize={28}
              />
            </View>
          )}
        </Animated.View>
      </TouchableWithoutFeedback>
    </View>
  );
};

class VerseProps {
  showFormatting: boolean;
  showHeadings: boolean;
  showFootnotes: boolean;
  isSelected: boolean;
  translationId: number;
  verseApiId: string;

  rangeEndVerseNumber?: number; // @NOTE: there is no need for a rangeStartVerseNumber because the PassageDisplay component will always send that as the verseApiId into this component
  isForContext?: boolean;
  skipFetch?: boolean; // true if fetching is handled by an ancestor
  onPressVerse?: () => void; // @TODO: verify signature
  isPressDisabled?: boolean;
  isSelectionModeOn?: boolean;
  defaultTextColor?: 'white' | 'primaryExtraDark' = 'white'; // @TODO: do the same thing for verse numbers
  isLoading?: boolean;
  verseNumbersOnly?: boolean;
  showContextHighlight?: boolean;
  textColorOverride?: string;
  numsOnlyStyle?: any;
  verseTextStyles?: any;
  isBadgePressDisabled?: boolean;
  showWrinkleBadge?: boolean;
  hideProgressStyling?: boolean;
};
const defaultProps = new VerseProps();

export default Verse;
